import React, { useEffect, useRef, useState } from "react";
import { MessageCircle, X, Send, Sparkles } from "lucide-react";
import { API_BASE } from "@/lib/api";

const getSessionId = () => {
  let sid = localStorage.getItem("itsp_chat_sid");
  if (!sid) {
    sid = `web-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
    localStorage.setItem("itsp_chat_sid", sid);
  }
  return sid;
};

const WELCOME = {
  role: "assistant",
  content: "Hallo! Ich bin der KI-Assistent von IT-Servicepoint. Fragen Sie mich gern zu unseren Leistungen – Managed Services, IT-Sicherheit, Webdesign oder Überwachungskameras. Wie kann ich helfen?",
};

export const ChatWidget = () => {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([WELCOME]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const bodyRef = useRef(null);
  const loadedRef = useRef(false);

  useEffect(() => {
    if (!open || loadedRef.current) return;
    loadedRef.current = true;
    fetch(`${API_BASE}/ai/chat/${getSessionId()}`)
      .then((r) => r.json())
      .then((hist) => {
        if (Array.isArray(hist) && hist.length) setMessages([WELCOME, ...hist]);
      })
      .catch(() => {});
  }, [open]);

  useEffect(() => {
    if (bodyRef.current) bodyRef.current.scrollTop = bodyRef.current.scrollHeight;
  }, [messages, open]);

  const send = async (e) => {
    e?.preventDefault();
    const text = input.trim();
    if (!text || busy) return;
    setInput("");
    setBusy(true);
    setMessages((m) => [...m, { role: "user", content: text }, { role: "assistant", content: "" }]);
    try {
      const res = await fetch(`${API_BASE}/ai/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_id: getSessionId(), message: text }),
      });
      if (!res.ok || !res.body) throw new Error("chat failed");
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split("\n\n");
        buffer = lines.pop() || "";
        for (const line of lines) {
          if (!line.startsWith("data: ")) continue;
          const payload = line.slice(6);
          if (payload === "[DONE]") continue;
          try {
            const obj = JSON.parse(payload);
            const chunk = obj.delta || obj.error || "";
            if (chunk) {
              setMessages((m) => {
                const copy = [...m];
                copy[copy.length - 1] = { role: "assistant", content: copy[copy.length - 1].content + chunk };
                return copy;
              });
            }
          } catch { /* skip malformed */ }
        }
      }
    } catch {
      setMessages((m) => {
        const copy = [...m];
        copy[copy.length - 1] = {
          role: "assistant",
          content: "Der Assistent ist gerade nicht erreichbar. Rufen Sie uns gern direkt an: +49 (0) 6629 9153890.",
        };
        return copy;
      });
    }
    setBusy(false);
  };

  return (
    <>
      {open && (
        <div
          data-testid="chat-panel"
          className="fixed bottom-36 right-4 sm:right-6 z-[60] w-[calc(100vw-2rem)] max-w-sm border border-line bg-white shadow-2xl flex flex-col"
          style={{ height: "min(520px, calc(100vh - 200px))" }}
        >
          <div className="bg-ink text-white px-5 py-4 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Sparkles size={16} className="text-accent" />
              <div>
                <div className="font-display font-bold leading-tight">IT-Assistent</div>
                <div className="font-mono text-[9px] uppercase tracking-[0.2em] text-white/60">Claude AI · antwortet sofort</div>
              </div>
            </div>
            <button data-testid="chat-close-btn" onClick={() => setOpen(false)} className="text-white/70 hover:text-white" aria-label="Chat schließen">
              <X size={18} />
            </button>
          </div>
          <div ref={bodyRef} className="flex-1 overflow-y-auto p-4 space-y-3 bg-soft">
            {messages.map((m, i) => (
              <div key={i} data-testid={`chat-message-${m.role}`} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                <div className={`max-w-[85%] px-4 py-2.5 text-sm leading-relaxed whitespace-pre-line ${
                  m.role === "user" ? "bg-primary text-white" : "bg-white border border-line text-ink"
                }`}>
                  {m.content || (busy && i === messages.length - 1 ? <span className="inline-flex gap-1"><span className="animate-pulse">●</span><span className="animate-pulse" style={{ animationDelay: "0.2s" }}>●</span><span className="animate-pulse" style={{ animationDelay: "0.4s" }}>●</span></span> : "")}
                </div>
              </div>
            ))}
          </div>
          <form onSubmit={send} className="border-t border-line p-3 flex gap-2 bg-white">
            <input
              data-testid="chat-input"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Ihre Frage…"
              maxLength={2000}
              className="flex-1 border border-line px-3 py-2.5 text-sm outline-none focus:border-primary"
            />
            <button
              data-testid="chat-send-btn"
              type="submit"
              disabled={busy || !input.trim()}
              className="px-4 bg-accent text-ink hover:bg-accent/90 disabled:opacity-40 transition-colors"
              aria-label="Senden"
            >
              <Send size={16} />
            </button>
          </form>
          <div className="px-4 pb-2 bg-white font-mono text-[9px] text-muted">
            KI-generierte Antworten – ohne Gewähr. Verbindlich: +49 (0) 6629 9153890
          </div>
        </div>
      )}
      <button
        data-testid="chat-toggle-btn"
        onClick={() => setOpen((o) => !o)}
        className="fixed bottom-20 right-4 sm:right-6 z-[60] w-14 h-14 bg-primary text-white shadow-glow flex items-center justify-center hover:bg-primary-hover transition-all hover:scale-105"
        aria-label={open ? "Chat schließen" : "Chat öffnen"}
      >
        {open ? <X size={22} /> : <MessageCircle size={22} />}
      </button>
    </>
  );
};
